import React, { useState, useEffect, useRef } from "react";
import { SectorData } from "@labci/bell-fg-widgets";

const SectorAnalysis: React.FC = () => {
  const [isDarkTheme, setIsDarkTheme] = useState(true);

  const queryParams = new URLSearchParams(window.location.search);
  const [region, setRegion] = useState(queryParams.get("region") || "AU");
  const [period, setPeriod] = useState(queryParams.get("period") || "1M"); //1D,1M, 3M, 6M, 1YR
  const [sector, setSector] = useState("50"); //50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60 , 61, 62, 63
  const token = queryParams.get("token") || "";
  const theme = queryParams.get("theme") || "direct";
  //const tab = queryParams.get('tab') || ""; //summary, performance, technical, fundamentals, companyscore, ratings
  const [tab, setTab] = useState("summary");

  const peerComparisonIframeRef = useRef<HTMLIFrameElement>(null);
  const sectorIndicesIframeRef = useRef<HTMLIFrameElement>(null);
  const sectorHeatmapIframeRef = useRef<HTMLIFrameElement>(null);

  // Get base URL for iframe src
  const getBaseUrl = () => {
    const basePath = process.env.REACT_APP_ROUTER_BASE || "/bellfgdev";
    console.log("getBaseUrl", `${window.location.origin}/${basePath}`);
    return `${window.location.origin}/${basePath}`;
  };

  // Build iframe URLs
  const getSectorIndicesUrl = () => {
    const params = new URLSearchParams({
      region,
      token,
      period,
      theme,
    });
    console.log(
      "getSectorIndicesUrl",
      `${getBaseUrl()}/sectorindices?${params.toString()}`,
    );
    return `${getBaseUrl()}/sectorindices?${params.toString()}`;
  };

  const getSectorHeatmapUrl = () => {
    const params = new URLSearchParams({
      region,
      token,
      period,
      theme,
    });
    console.log(
      "getSectorHeatmapUrl",
      `${getBaseUrl()}/sectorheatmap?${params.toString()}`,
    );
    return `${getBaseUrl()}/sectorheatmap?${params.toString()}`;
  };

  const getSectorPeerComparisonUrl = () => {
    const params = new URLSearchParams({
      region,
      sector,
      token,
      tab,
      theme,
    });
    console.log(
      "getSectorPeerComparisonUrl",
      `${getBaseUrl()}/sectorpeercomparison?${params.toString()}`,
    );
    return `${getBaseUrl()}/sectorpeercomparison?${params.toString()}`;
  };

  // Handle postMessage from iframes
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin if needed (for security)
      // if (event.origin !== window.location.origin) return;

      console.log("handleMessage", event);
      try {
        // Widgets send JSON stringified messages, so parse if needed
        let data = event.data;
        if (typeof data === "string") {
          data = JSON.parse(data);
        }

        if (data && data.type === "sector") {
          const msg = data as SectorData;
          if (msg && msg.data && msg.data.sector) {
            setSector(msg.data.sector);
          }
        }
      } catch (error) {
        console.error("Error parsing message:", error);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Send sector and tab updates to SectorPeerComparison iframe
  useEffect(() => {
    if (
      peerComparisonIframeRef.current &&
      peerComparisonIframeRef.current.contentWindow
    ) {
      peerComparisonIframeRef.current.contentWindow.postMessage(
        {
          type: "sector",
          data: { sector },
        },
        "*",
      );
    }
  }, [sector]);

  useEffect(() => {
    if (
      peerComparisonIframeRef.current &&
      peerComparisonIframeRef.current.contentWindow
    ) {
      peerComparisonIframeRef.current.contentWindow.postMessage(
        {
          type: "tab",
          data: { tab },
        },
        "*",
      );
    }
  }, [tab]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "white",
        boxSizing: "border-box",
        overflow: "hidden",
        minWidth: "375px",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "10px",
          justifyContent: "flex-start",
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <select value={region} onChange={(e) => setRegion(e.target.value)}>
          <option value="AU">AU</option>
          <option value="US">US</option>
        </select>
        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="1D">1D</option>
          <option value="1M">1M</option>
          <option value="3M">3M</option>
          <option value="6M">6M</option>
          <option value="1YR">1YR</option>
        </select>
      </div>
      {/* First row: SectorIndices and SectorHeatmap iframes */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          marginBottom: "20px",
          flex: "0 1 auto",
          width: "100%",
        }}
      >
        {/*<div style={{ flex: '1 1 50%', minWidth: 0, height: '45vh', maxHeight: '45vh', overflow: 'hidden', border: '1px solid #ccc' }}>
          <iframe
            ref={sectorIndicesIframeRef}
            key={`sector-indices-${region}-${period}-${token}`}
            src={getSectorIndicesUrl()}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Sector Indices"
          />
        </div>*/}
        <div
          style={{
            flex: "1 1 50%",
            minWidth: 0,
            height: "45vh",
            maxHeight: "45vh",
            overflow: "hidden",
            border: "1px solid #ccc",
          }}
        >
          <iframe
            ref={sectorHeatmapIframeRef}
            key={`sector-heatmap-${region}-${period}-${token}`}
            src={getSectorHeatmapUrl()}
            style={{ width: "100%", height: "100%", border: "none" }}
            title="Sector Heatmap"
          />
        </div>
      </div>
      {/* Second row: SectorPeerComparison iframe */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* use a htmlto change tab: summary, performance, technical, fundamentals, companyscore, ratings */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <button onClick={() => setTab("summary")}>Summary</button>
          <button onClick={() => setTab("performance")}>Performance</button>
          <button onClick={() => setTab("technical")}>Technical</button>
          <button onClick={() => setTab("fundamentals")}>Fundamentals</button>
          <button onClick={() => setTab("companyscore")}>Company Score</button>
          <button onClick={() => setTab("ratings")}>Ratings</button>
        </div>
        <div
          style={{
            flex: "1 1 auto",
            overflow: "hidden",
            minHeight: 0,
            width: "100%",
            border: "1px solid #ccc",
          }}
        >
          <iframe
            ref={peerComparisonIframeRef}
            key={`sector-peer-comparison-${region}-${sector}-${tab}-${token}`}
            src={getSectorPeerComparisonUrl()}
            style={{ width: "100%", height: "50vh", border: "none" }}
            title="Sector Peer Comparison"
          />
        </div>
      </div>
    </div>
  );
};

export default SectorAnalysis;
